<script lang="ts">
  import TopMenu from "../../components/TopMenu.svelte";
  import { parseDate } from "../../lib/parseDate";
  import Spinner from "../../components/UI/Spinner.svelte";
  import MealExpansionPanel from "../../components/Diet/MealExpansionPanel.svelte";
  import { dialogData } from "../../stores/dialogDataStore";
  import { onMount } from "svelte";
  import {
    dialogTypes,
    getWithJwt,
    resolveMacroGoals,
    serverlessRoutes,
    translate,
    type MacroGoals,
  } from "lib";
  import Macros from "../../components/Diet/Macros.svelte";
  import { Capacitor } from "@capacitor/core";
  import { ButtonComponent, InfiniteScroll } from "ui";
  import { activeMealsStore, journalMealsStore, user } from "stores";
  import type { Meal } from "interfaces";

  let isLoading = false;
  let macrosLatest: MacroGoals | null = null;
  let todayMeals: Array<Meal> = [];
  let todayTotals = { calories: 0, carbs: 0, fats: 0, protein: 0 };
  let hasMealsToday = false;
  let remainingMacros = { calories: 0, carbs: 0, fats: 0, protein: 0 };

  const todayKey = new Date().toISOString().slice(0, 10);

  const calculateMealTotals = (meals: Array<Meal> = []) =>
    meals.reduce(
      (totals, meal) => {
        totals.calories += meal.calories || 0;
        totals.carbs += meal.carbs || 0;
        totals.fats += meal.fats || 0;
        totals.protein += meal.protein || 0;
        return totals;
      },
      { calories: 0, carbs: 0, fats: 0, protein: 0 }
    );

  const clampRemaining = (value: number): number => (value < 0 ? 0 : value);
  const formatMacroValue = (value: number): string => (value ? value.toFixed(1) : "0");

  const fetchMacros = async (): Promise<void> => {
    const url = `${serverlessRoutes.MACRO}?clientId=${$user.id}`;
    try {
      const response = await getWithJwt(url);
      macrosLatest = resolveMacroGoals(response?.data);
    } catch (err) {}
  };

  $: todayMeals = $journalMealsStore.map?.[todayKey] || [];
  $: todayTotals = calculateMealTotals(todayMeals);
  $: hasMealsToday = todayMeals.length > 0;
  $: remainingMacros = hasMealsToday
    ? {
        calories: clampRemaining((macrosLatest?.calories || 0) - todayTotals.calories),
        carbs: clampRemaining((macrosLatest?.carbs || 0) - todayTotals.carbs),
        fats: clampRemaining((macrosLatest?.fats || 0) - todayTotals.fats),
        protein: clampRemaining((macrosLatest?.protein || 0) - todayTotals.protein),
      }
    : {
        calories: macrosLatest?.calories || 0,
        carbs: macrosLatest?.carbs || 0,
        fats: macrosLatest?.fats || 0,
        protein: macrosLatest?.protein || 0,
      };

  const onFetchMoreDiary = async () => {
    await journalMealsStore.fetchMoreData();
    journalMealsStore.generateMealMap();
  };

  const onExportToPDF = async (): Promise<void> => {
    $dialogData.data = {
      url: `${serverlessRoutes.MEAL_PLANS}/pdf`,
      clientId: $user.id,
      fileName: "meal-plan.pdf"
    };

    $dialogData.type = dialogTypes.PDF_DIALOG;
  };

  onMount(async () => {
    isLoading = true;
    await Promise.all([journalMealsStore.fetchData(), fetchMacros()]);
    journalMealsStore.generateMealMap();
    isLoading = false;
    await activeMealsStore.fetchData();
  });
</script>

<TopMenu entity="DIET" />
<div class="p-4 flex flex-col gap-4 bg-white dark:bg-zinc-800 text-slate-900 dark:text-slate-100">
  {#if isLoading}
    <Spinner />
  {:else}
    {#if macrosLatest}
      <div class="mb-4 p-2 border rounded-md border-slate-200 dark:border-zinc-600 text-center">
        <div class="mb-4">
          {hasMealsToday
            ? translate("REMAINING_MACROS_TODAY")
            : translate("SUGGESTED_MACROS")}
          <span class="text-amber-400">{formatMacroValue(remainingMacros.calories)}</span>
          kcal
        </div>
        <Macros
          carbs={remainingMacros.carbs}
          fats={remainingMacros.fats}
          calories={remainingMacros.calories}
          protein={remainingMacros.protein}
          isBarVisible
        />
      </div>
    {/if}
    <div class="flex justify-between">
      {#if $activeMealsStore.items.length}
        <ButtonComponent
          on:click={() => {
            $dialogData.type = dialogTypes.CREATE_EDIT_MEAL;
            $dialogData.data = {
              mealPlanId: $activeMealsStore.items[0]?.mealPlanId,
              // fetchData: activeMealsStore.fetchData,
            };
          }}
        >
          {translate("ADD_MEAL")}
        </ButtonComponent>
      {/if}
      {#if true}
        <ButtonComponent on:click={onExportToPDF}>
          {translate("EXPORT_TO_PDF")}
        </ButtonComponent>
      {/if}
    </div>
    <div class="flex flex-col gap-4">
      {#each Object.keys($journalMealsStore.map).sort((a, b) => new Date(b).getTime() - new Date(a).getTime()).map(date => $journalMealsStore.map[date]) as day}
        <span class="text-xs font-semibold">{parseDate(day[0].finishedAt)}</span>
        <div
          class="flex flex-col gap-4 p-2 border border-slate-200 dark:border-zinc-600 rounded-md"
        >
          <div class="p-1 flex flex-col">
            <Macros
              isCaloriesVisible
              isBarVisible
              calories={day.reduce((sum, i) => (sum += i.calories), 0)}
              carbs={day.reduce((sum, i) => (sum += i.carbs), 0)}
              fats={day.reduce((sum, i) => (sum += i.fats), 0)}
              protein={day.reduce((sum, i) => (sum += i.protein), 0)}
            />
          </div>
          {#each day as meal}
            <MealExpansionPanel isCommentable {meal} />
          {/each}
        </div>
      {/each}
    </div>
    <InfiniteScroll
      hasMore={$journalMealsStore.hasMore}
      isFetchingMore={$journalMealsStore.isFetchingMore}
      on:loadMore={onFetchMoreDiary}
    />

    {#if Capacitor.getPlatform() === "ios"}
      <div class="mb-14"></div>
    {/if}
  {/if}
  {#if Capacitor.isNativePlatform()}
    <div class="mb-12"></div>
  {/if}
  {#if Capacitor.getPlatform() === "ios"}
    <div class="pb-16"></div>
  {/if}
</div>
